import React, { useEffect, useState } from "react";
import { Mail, Linkedin, Twitter } from "lucide-react";
import "./Contact.css";

const API_BASE =
  (process.env?.REACT_APP_API_BASE) ||
  import.meta?.env?.VITE_API_BASE ||
  "https://127.0.0.1:8000";

console.log("process.env?.REACT_APP_API_BASE", process.env?.REACT_APP_API_BASE)
console.log(typeof process)
console.log(API_BASE)

const Contact = () => {
   useEffect(() => {
    document.title = "Contact InnoRock – Talk to Us About Automation";

    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Contact InnoRock to explore automation for your business. Share your workflows and we’ll show what can be automated."
      );
    }

    const canonical = document.querySelector("link[rel='canonical']");
    if (canonical) {
      canonical.setAttribute("href", "https://innorock.org/#contact");
    }
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverMsg, setServerMsg] = useState(null); // success/error banner

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = "Name is required";
    if (!formData.company.trim()) errs.company = "Company is required";

    // Basic email validity
    if (!/^\S+@\S+\.\S+$/.test(formData.email.trim())) {
      errs.email = "Valid email is required";
    }

    // Phone: your backend accepts up to 20 chars; front-end check for 10+ digits
    const digitsOnly = formData.phone.replace(/\D/g, "");
    if (!digitsOnly || digitsOnly.length < 10) {
      errs.phone = "Valid phone number is required";
    }

    if (!formData.message.trim() || formData.message.trim().length < 5) {
      errs.message = "Message must be at least 5 characters";
    }
    return errs;
  };

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    // Clear per-field error when user edits
    if (errors[field]) {
      setErrors((prev) => {
        const copy = { ...prev };
        delete copy[field];
        return copy;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerMsg(null);

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setErrors({});
    setSubmitting(true);

    try {
      const response = await fetch(`${API_BASE}/api/submit-contact/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // If you need cookies/CSRF in the future: credentials: "include",
        body: JSON.stringify(formData),
      });

      // Safely try to parse JSON; backend may return HTML on 500
      let result = null;
      try {
        result = await response.json();
      } catch {
        /* ignore parse errors */
      }

      if (response.ok) {
        setServerMsg({ type: "success", text: "Your message has been submitted!" });
        setFormData({ name: "", company: "", email: "", phone: "", message: "" });
      } else {
        const msg =
          (result && (result.error || result.detail || result.message)) ||
          `Request failed (${response.status} ${response.statusText})`;
        setServerMsg({ type: "error", text: msg });
        console.error("Backend error:", result || msg);
      }
    } catch (err) {
      console.error("Network error:", err);
      setServerMsg({ type: "error", text: "Could not connect to the server." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact-section">
      <div className="contact-container">
        <div className="contact-inner">
          {/* Header */}
          <div className="contact-header">
            <h2 className="contact-title">Let&apos;s Talk About Your AI Journey</h2>
            <p className="contact-subtitle">
              Ready to transform your business with AI? Get in touch for a free consultation.
            </p>
          </div>

          {/* Optional server banner */}
          {serverMsg && (
            <div
              role="status"
              className={`contact-banner ${serverMsg.type === "error" ? "is-error" : "is-success"}`}
            >
              {serverMsg.text}
            </div>
          )}

          {/* Grid */}
          <div className="contact-grid">
            {/* Form Card */}
            <div className="contact-card contact-form-card">
              <form className="contact-form" onSubmit={handleSubmit} noValidate>
                <div className="form-field">
                  <label htmlFor="name" className="form-label">Name</label>
                  <input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange("name")}
                    className={`form-input ${errors.name ? "has-error" : ""}`}
                    placeholder="Your name"
                    aria-invalid={!!errors.name}
                    aria-describedby={errors.name ? "err-name" : undefined}
                    required
                  />
                  {errors.name && <p id="err-name" className="error">{errors.name}</p>}
                </div>

                <div className="form-field">
                  <label htmlFor="company" className="form-label">Company Name</label>
                  <input
                    id="company"
                    type="text"
                    value={formData.company}
                    onChange={handleChange("company")}
                    className={`form-input ${errors.company ? "has-error" : ""}`}
                    placeholder="Your company"
                    aria-invalid={!!errors.company}
                    aria-describedby={errors.company ? "err-company" : undefined}
                    required
                  />
                  {errors.company && <p id="err-company" className="error">{errors.company}</p>}
                </div>

                <div className="form-field">
                  <label htmlFor="email" className="form-label">Email</label>
                  <input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange("email")}
                    className={`form-input ${errors.email ? "has-error" : ""}`}
                    placeholder="you@company.com"
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? "err-email" : undefined}
                    required
                  />
                  {errors.email && <p id="err-email" className="error">{errors.email}</p>}
                </div>

                <div className="form-field">
                  <label htmlFor="phone" className="form-label">Phone</label>
                  <input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange("phone")}
                    className={`form-input ${errors.phone ? "has-error" : ""}`}
                    placeholder="+1 (555) 000-0000"
                    aria-invalid={!!errors.phone}
                    aria-describedby={errors.phone ? "err-phone" : undefined}
                    required
                  />
                  {errors.phone && <p id="err-phone" className="error">{errors.phone}</p>}
                </div>

                <div className="form-field">
                  <label htmlFor="message" className="form-label">Message</label>
                  <textarea
                    id="message"
                    rows="6"
                    value={formData.message}
                    onChange={handleChange("message")}
                    className={`form-textarea ${errors.message ? "has-error" : ""}`}
                    placeholder="Tell us about your business and how we can help..."
                    aria-invalid={!!errors.message}
                    aria-describedby={errors.message ? "err-message" : undefined}
                    required
                  />
                  {errors.message && <p id="err-message" className="error">{errors.message}</p>}
                </div>

                <button
                  type="submit"
                  className="btn-accent"
                  disabled={submitting}
                  aria-busy={submitting}
                >
                  {submitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            </div>

            {/* Aside Card */}
            <div className="contact-aside">
              <div className="contact-card">
                <div className="contact-row">
                  <div className="icon-box">
                    <Mail className="icon" />
                  </div>
                  <div>
                    <div className="meta-label">Email</div>
                    <a href="mailto:suraj@innorock.org" className="meta-link">
                      suraj@innorock.org
                    </a>
                  </div>
                </div>
              </div>

              <div className="contact-card">
                <h3 className="aside-title">Follow Us</h3>
                <div className="socials">
                  <a
                    href="https://www.linkedin.com/company/innorock"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-btn"
                    aria-label="LinkedIn"
                  >
                    <Linkedin className="icon" />
                  </a>
                  <a
                    href="https://twitter.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-btn"
                    aria-label="Twitter / X"
                  >
                    <Twitter className="icon" />
                  </a>
                </div>
              </div>
            </div>
            {/* /aside */}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
